import React, { useEffect, useRef, useState, useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { useSound } from '../../hooks/useSound';
import './GameCanvas.css';

// ─── SVG Rabbit ──────────────────────────────────────────────────────────────
function RabbitSVG({ state }) {
  const isHopping   = state === 'hop';
  const isFailing   = state === 'fail';
  const isWinning   = state === 'win';
  const isWaiting   = state === 'wait';
  const isJumpStart = state === 'jump-start';
  const isDrop      = state === 'drop';

  const cls = [
    'rabbit-svg',
    isHopping   && 'hop',
    isFailing   && 'fail-anim',
    isWinning   && 'win-anim',
    isWaiting   && 'rabbit-idle',
    isJumpStart && 'rabbit-jump-start',
    isDrop      && 'rabbit-drop',
  ].filter(Boolean).join(' ');

  const spreadLegs = isHopping || isJumpStart;

  return (
    <svg viewBox="0 0 60 72" width="134" height="162" className={cls}>
      {/* Body */}
      <ellipse cx="30" cy="52" rx="18" ry="16" fill="#f0e0ff" />
      {/* Head */}
      <circle cx="30" cy="34" r="14" fill="#f0e0ff" />
      {/* Left ear */}
      <ellipse className="rabbit-ear-left" cx="22" cy="14" rx="5" ry="13" fill="#f0e0ff" />
      <ellipse className="rabbit-ear-left-inner" cx="22" cy="14" rx="3" ry="10" fill="#ffc0cb" />
      {/* Right ear */}
      <ellipse className="rabbit-ear-right" cx="38" cy="14" rx="5" ry="13" fill="#f0e0ff" />
      <ellipse className="rabbit-ear-right-inner" cx="38" cy="14" rx="3" ry="10" fill="#ffc0cb" />
      {/* Eyes */}
      <circle className="rabbit-eye rabbit-eye-left" cx="25" cy="32" r="3" fill="#1a0533" />
      <circle className="rabbit-eye rabbit-eye-right" cx="35" cy="32" r="3" fill="#1a0533" />
      <circle cx="26" cy="31" r="1" fill="white" />
      <circle cx="36" cy="31" r="1" fill="white" />
      {/* Nose */}
      <ellipse cx="30" cy="37" rx="2.5" ry="1.5" fill="#ff9eb5" />
      {/* Smile */}
      <path d="M26 39 Q30 43 34 39" stroke="#1a0533" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Cheeks */}
      <circle cx="22" cy="37" r="3" fill="#ffb0c8" opacity="0.6" />
      <circle cx="38" cy="37" r="3" fill="#ffb0c8" opacity="0.6" />
      {/* Tail */}
      <circle cx="46" cy="56" r="6" fill="white" />
      {/* Front legs */}
      <ellipse cx={spreadLegs ? 20 : 22} cy="65" rx="5" ry={spreadLegs ? 3 : 5} fill="#e8d0f8" />
      <ellipse cx={spreadLegs ? 40 : 38} cy="65" rx="5" ry={spreadLegs ? 3 : 5} fill="#e8d0f8" />
      {/* Star on fail */}
      {isFailing && (
        <text x="44" y="22" fontSize="14" textAnchor="middle">💥</text>
      )}
      {isWinning && (
        <text x="44" y="22" fontSize="14" textAnchor="middle">⭐</text>
      )}
    </svg>
  );
}

// ─── Carrot SVG ──────────────────────────────────────────────────────────────
function Carrot({ className }) {
  return (
    <svg width="72" height="72" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M74 22C78 10 89 5 101 8C96 15 91 21 84 25C94 22 105 25 112 34C101 36 92 35 84 30C91 38 93 48 90 58C80 51 75 43 74 32C70 41 62 48 52 51C53 39 60 28 74 22Z" fill="#38A169"/>
      <path d="M85 29C91 34 93 43 89 52C80 48 75 41 74 32L85 29Z" fill="#2F855A"/>
      <path d="M23 113C16 107 18 95 24 82L48 31C54 18 71 16 80 27C89 38 84 54 74 64L35 107C31 112 26 116 23 113Z" fill="#F97316"/>
      <path d="M48 31C54 18 71 16 80 27C83 31 84 36 83 41C73 35 59 38 53 50L28 102C25 108 24 112 23 113C16 107 18 95 24 82L48 31Z" fill="#FB923C"/>
      <path d="M42 58L61 51" stroke="#C2410C" strokeWidth="5" strokeLinecap="round"/>
      <path d="M34 76L50 70" stroke="#C2410C" strokeWidth="5" strokeLinecap="round"/>
      <path d="M27 94L39 89" stroke="#C2410C" strokeWidth="5" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Cabbage SVG ─────────────────────────────────────────────────────────────
function Cabbage({ className }) {
  return (
    <svg width="70" height="70" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <circle cx="64" cy="66" r="45" fill="#86EFAC"/>
      <path d="M28 67C27 43 45 22 70 23C87 24 101 37 106 53C94 45 77 45 64 55C49 66 42 83 46 102C35 95 29 82 28 67Z" fill="#4ADE80"/>
      <path d="M105 57C111 80 96 105 72 110C53 114 35 106 26 91C42 98 60 95 72 82C83 70 91 61 105 57Z" fill="#22C55E"/>
      <path d="M41 54C50 35 77 32 91 48C76 48 64 56 58 69C50 64 45 59 41 54Z" fill="#BBF7D0"/>
      <path d="M48 88C52 69 68 55 88 55C83 73 69 87 48 88Z" fill="#BBF7D0"/>
      <path d="M33 72C45 70 56 76 63 88C51 90 39 84 33 72Z" fill="#16A34A" opacity="0.55"/>
      <path d="M64 28C58 45 58 63 65 84" stroke="#15803D" strokeWidth="4" strokeLinecap="round" opacity="0.6"/>
      <path d="M33 64C48 66 62 72 75 85" stroke="#15803D" strokeWidth="4" strokeLinecap="round" opacity="0.55"/>
      <path d="M96 57C82 61 70 70 60 86" stroke="#15803D" strokeWidth="4" strokeLinecap="round" opacity="0.55"/>
    </svg>
  );
}

// ─── Broccoli SVG ────────────────────────────────────────────────────────────
function Broccoli({ className }) {
  return (
    <svg width="70" height="70" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M57 60H72L78 111C79 116 75 121 70 121H58C53 121 49 116 50 111L57 60Z" fill="#86EFAC"/>
      <path d="M64 60V121" stroke="#22C55E" strokeWidth="5" strokeLinecap="round"/>
      <path d="M57 89C48 84 39 79 34 70" stroke="#22C55E" strokeWidth="5" strokeLinecap="round"/>
      <path d="M71 88C81 83 89 77 94 68" stroke="#22C55E" strokeWidth="5" strokeLinecap="round"/>
      <circle cx="40" cy="50" r="24" fill="#16A34A"/>
      <circle cx="63" cy="35" r="27" fill="#22C55E"/>
      <circle cx="88" cy="51" r="25" fill="#15803D"/>
      <circle cx="61" cy="59" r="28" fill="#16A34A"/>
      <circle cx="45" cy="42" r="13" fill="#4ADE80" opacity="0.75"/>
      <circle cx="68" cy="28" r="12" fill="#86EFAC" opacity="0.65"/>
      <circle cx="87" cy="43" r="12" fill="#4ADE80" opacity="0.55"/>
      <circle cx="61" cy="58" r="11" fill="#4ADE80" opacity="0.55"/>
    </svg>
  );
}

// ─── Main Canvas ─────────────────────────────────────────────────────────────
export default function GameCanvas() {
  const { gameState, currentStep, currentMultiplier, possibleWin,
          betAmount, multiplierTable, animationsEnabled, failedStep, lastOutcome } = useGameStore();
  const { play } = useSound();

  const [rabbitState, setRabbitState] = useState(gameState === 'idle' ? 'wait' : 'idle');
  const [veggies, setVeggies]         = useState([]);
  const [coins, setCoins]             = useState([]);
  const [isDragging, setIsDragging]   = useState(false);
  const veggieTimer      = useRef(null);
  const pathRef          = useRef(null);
  const dragState        = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const prevStateRef     = useRef(gameState);
  const rabbitTimer      = useRef(null);
  const wasRoundActiveRef    = useRef(false);
  const roundJustStartedRef  = useRef(false);
  const showCornerRef        = useRef(gameState === 'idle');
  const justDroppedRef       = useRef(false);
  const dropSoundTimer       = useRef(null);
  // Trigger rabbit anim on game state change
  useEffect(() => {
    prevStateRef.current = gameState;

    if (rabbitTimer.current)   clearTimeout(rabbitTimer.current);
    if (dropSoundTimer.current) clearTimeout(dropSoundTimer.current);

    if (gameState === 'idle') {
      justDroppedRef.current = false;
      setRabbitState('wait');
      return;
    }
    if (gameState === 'round_started') {
      justDroppedRef.current = true;
      setRabbitState('drop');
      dropSoundTimer.current = setTimeout(() => play('step'), 200);
      rabbitTimer.current    = setTimeout(() => setRabbitState('idle'), 500);
      return;
    }
    if (gameState === 'step_success') {
      if (justDroppedRef.current) {
        justDroppedRef.current = false;
        rabbitTimer.current = setTimeout(() => setRabbitState('idle'), 300);
        return;
      }
      play('step');
      setRabbitState('hop');
      rabbitTimer.current = setTimeout(() => setRabbitState('idle'), 600);
      return;
    }
    if (gameState === 'step_failed') { justDroppedRef.current = false; setRabbitState('fail'); return; }
    if (gameState === 'cashed_out')  { justDroppedRef.current = false; setRabbitState('win'); spawnCoins(); return; }
  }, [gameState, play]);

  // Spawn flying vegetables
  useEffect(() => {
    if (!animationsEnabled) { setVeggies([]); return; }
    veggieTimer.current = setInterval(() => {
      const types   = ['carrot', 'cabbage', 'broccoli'];
      const type    = types[Math.floor(Math.random() * types.length)];
      const id      = Math.random().toString(36).slice(2);
      const top     = 10 + Math.random() * 55;
      const speed   = 4 + Math.random() * 5;
      setVeggies(v => [...v.slice(-6), { id, type, top, speed }]);
    }, 2500);
    return () => clearInterval(veggieTimer.current);
  }, [animationsEnabled]);

  function spawnCoins() {
    const c = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      tx: `${(Math.random() - 0.5) * 200}px`,
      ty: `${-80 - Math.random() * 120}px`,
      delay: i * 60,
    }));
    setCoins(c);
    setTimeout(() => setCoins([]), 1800);
  }

  const stepItems = multiplierTable.length > 0
    ? multiplierTable
    : Array.from({ length: 10 }, (_, i) => ({ level: i + 1, multiplier: 1 }));

  const stars = useMemo(() => Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: `${Math.random() * 3}s`,
    opacity: Math.random() * 0.7 + 0.3,
  })), []);

  const isRoundActive = gameState === 'round_started' || gameState === 'step_success';
  const isIdleWait    = gameState === 'idle' || gameState === 'round_completed';

  // Track whether the last active state was step_success (not round_started).
  // Used to keep the card rabbit and info bar visible during the brief
  // waiting_for_server between steps without flashing.
  const wasStepRef = wasRoundActiveRef; // reuse the existing ref
  if (isIdleWait) {
    showCornerRef.current = true;
  }
  if (gameState === 'round_started') {
    wasStepRef.current = false;
    roundJustStartedRef.current = true;
    showCornerRef.current = false;
  }
  if (gameState === 'step_success') {
    wasStepRef.current = true;
    roundJustStartedRef.current = false;
  }
  if (gameState === 'step_failed' || gameState === 'cashed_out') {
    wasStepRef.current = false;
    roundJustStartedRef.current = false;
  }

  // Server sends 1-based step numbers; card index = step - 1.
  // cashed_out keeps currentStep from last step_success so same formula applies.
  // step_failed stores failedStep in the same index space as isFailed (failedStep === i).
  const activeIndex =
    (gameState === 'step_success' || gameState === 'cashed_out')
      ? Math.max(0, currentStep - 1)
    : gameState === 'step_failed'
      ? (failedStep ?? 0)
    : (gameState === 'waiting_for_server' && wasStepRef.current)
      ? Math.max(0, currentStep - 1)
    : currentStep;
  const showRabbitOnCards = gameState === 'step_success' ||
    gameState === 'step_failed' ||
    gameState === 'cashed_out' ||
    gameState === 'round_started' ||
    (gameState === 'waiting_for_server' && (wasStepRef.current || roundJustStartedRef.current));
  const keepInfoVisible = gameState === 'step_success' ||
    (gameState === 'waiting_for_server' && (wasStepRef.current || roundJustStartedRef.current));

  // Derive card rabbit state directly for round_started to avoid the one-render
  // flash of the old state before the useEffect fires setRabbitState('drop').
  const cardRabbitState = gameState === 'round_started' ? 'drop' : rabbitState;

  // Show the first card's multiplier during the drop and while waiting for the server
  // to respond to the first step (currentMultiplier may still be 1 at that point).
  const showFirstCardMult = gameState === 'round_started' ||
    (gameState === 'waiting_for_server' && roundJustStartedRef.current);
  const infoMult = showFirstCardMult
    ? (stepItems[0]?.multiplier ?? currentMultiplier)
    : currentMultiplier;
  const infoWin = showFirstCardMult
    ? betAmount * (stepItems[0]?.multiplier ?? 1)
    : possibleWin;

  useEffect(() => {
    let raf;
    raf = requestAnimationFrame(() => {
      if (!pathRef.current) return;
      const activeCard = pathRef.current.querySelector('.step-card.active');
      if (!activeCard) return;
      const container = pathRef.current;
      const target = activeCard.offsetLeft - container.clientWidth / 2 + activeCard.clientWidth / 2;
      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [currentStep, multiplierTable.length]);

  useEffect(() => {
    if (!pathRef.current) return;
    if (gameState === 'idle' || gameState === 'step_failed' || gameState === 'cashed_out') {
      pathRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [gameState]);

  function startDrag(clientX) {
    if (!pathRef.current) return;
    dragState.current.active = true;
    dragState.current.startX = clientX;
    dragState.current.scrollLeft = pathRef.current.scrollLeft;
    setIsDragging(true);
  }

  function stopDrag() {
    dragState.current.active = false;
    setIsDragging(false);
  }

  function handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    startDrag(e.pageX);
  }

  function handleMouseMove(e) {
    if (!dragState.current.active || !pathRef.current) return;
    e.preventDefault();
    pathRef.current.scrollLeft = dragState.current.scrollLeft - (e.pageX - dragState.current.startX);
  }

  function handleTouchStart(e) {
    if (!e.touches?.length) return;
    startDrag(e.touches[0].pageX);
  }

  function handleTouchMove(e) {
    if (!dragState.current.active || !pathRef.current || !e.touches?.length) return;
    pathRef.current.scrollLeft = dragState.current.scrollLeft - (e.touches[0].pageX - dragState.current.startX);
  }

  function handleTouchEnd() {
    stopDrag();
  }

  return (
    <div className="game-canvas">
      {/* Sky background */}
      <div className="sky-bg">
        {stars.map(s => (
          <div key={s.id} className="star"
            style={{ left: s.left, top: s.top, animationDelay: s.delay, opacity: s.opacity }} />
        ))}
      </div>

      {/* Flying vegetables */}
      {veggies.map(v => (
        <div key={v.id} className="flying-obstacle"
          style={{ top: `${v.top}%`, animationDuration: `${v.speed}s` }}>
          {v.type === 'carrot'   && <Carrot   className="fv fv-carrot" />}
          {v.type === 'cabbage'  && <Cabbage  className="fv fv-cabbage" />}
          {v.type === 'broccoli' && <Broccoli className="fv fv-broccoli" />}
        </div>
      ))}

      {/* Corner rabbit: visible from idle through pre-round waiting, hidden instantly on round_started */}
      {showCornerRef.current && (
        <div className="rabbit-corner rabbit-chip--animated">
          <RabbitSVG state="wait" />
          <div className="rabbit-ground-shadow" />
        </div>
      )}

      {/* Multiplier path */}
      <div
        className={`multiplier-path-container${isDragging ? ' dragging' : ''}`}
        ref={pathRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="multiplier-path">
          {stepItems.map((step, i) => {
            const multiplier = typeof step === 'object' ? step.multiplier : step;
            const level = typeof step === 'object' ? step.level : i + 1;
            const isPassed = i < activeIndex;
            const isActive = (isRoundActive || (gameState === 'waiting_for_server' && roundJustStartedRef.current)) && i === activeIndex;
            const isFailed = lastOutcome === 'lose' && failedStep === i;
            const isCashedOut = gameState === 'cashed_out' && i === activeIndex;

            const showRabbit = showRabbitOnCards && i === activeIndex;
            const rabbitIsAnimated = cardRabbitState === 'wait' || cardRabbitState === 'drop';

            return (
              <div
                key={level}
                className={`step-card${isPassed ? ' passed' : ''}${isActive ? ' active' : ''}${isFailed ? ' failed' : ''}${isCashedOut ? ' cashed-out' : ''}`}
              >
                {showRabbit && (
                  <div className={`rabbit-chip${rabbitIsAnimated ? ' rabbit-chip--animated' : ''}`}>
                    <RabbitSVG state={cardRabbitState} />
                    <div className="rabbit-ground-shadow" />
                  </div>
                )}
                {isFailed && (
                  <div className="crash-effect anim-pop-in">💥</div>
                )}
                <div className="step-number-label">#{level}</div>
                <div className="step-meta">
                  <strong className="step-multiplier">{multiplier.toFixed(2)}×</strong>
                </div>
                {isFailed && <div className="step-note">CRASHED</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Coin burst */}
      {coins.map(c => (
        <div key={c.id} className="coin-burst"
          style={{ '--tx': c.tx, '--ty': c.ty, animationDelay: `${c.delay}ms` }}>
          🪙
        </div>
      ))}


      {/* Info bar */}
      <div className="canvas-info-bar">
        {isIdleWait && (
          <span className="info-label">Place your bet and press START</span>
        )}
        {(keepInfoVisible || gameState === 'round_started') && (
          <>
            <span className="info-mult">{infoMult.toFixed(2)}x</span>
            <span className="info-win">Win: {infoWin.toFixed(2)}</span>
          </>
        )}
        {gameState === 'waiting_for_server' && !keepInfoVisible && (
          <span className="info-label spin-text">⏳ Processing…</span>
        )}
        {gameState === 'step_failed' && (
          <span className="info-lose">💀 Crashed! Bet lost.</span>
        )}
        {gameState === 'cashed_out' && (
          <span className="info-win">🎉 Cashed out!</span>
        )}
      </div>
    </div>
  );
}
